/* eslint-disable react/prop-types */
import { useState } from 'react';
import {
    Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControlLabel, IconButton, MenuItem, Stack, Switch, TextField, Tooltip,
    Typography,
} from '@mui/material';
import { Iconify } from '../../../components';
import { PRICE_TYPES } from '../../../constants/PracticeTreatmentsCategoryConst';
import { updatePracticeTreatment } from '../../../redux/slices/practiceTreatment';
import { dispatch } from '../../../redux/store';

/** How the WhatsApp agent may STATE this treatment's price. Nothing else.
 *
 *  `price` stays exactly what it has always been, and every screen, report and
 *  integration that reads it is untouched. This adds a second, optional layer
 *  next to it: whether the figure is a fixed fee, a starting price or the
 *  bottom of a range, what it is charged per, and whether anybody at the
 *  practice has signed it off.
 *
 *  It lives behind a button in its own column on purpose. The first version of
 *  this put the controls in the price cell, and a practice opening Treatments
 *  found the screen it uses daily rearranged — and, because every row defaults
 *  to "on request", its prices apparently gone. A capability nobody asked for
 *  should cost nobody a second look.
 */

const fromRow = (row) => ({
    price_type: row.price_type || 'POA',
    price_max: row.price_max === null || row.price_max === undefined ? '' : String(row.price_max),
    price_unit: row.price_unit || '',
    price_note: row.price_note || '',
});

export default function AiPricingCell({ row, practiceId, onSaved }) {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(fromRow(row));
    const [saving, setSaving] = useState(false);

    const isSet = (row.price_type || 'POA') !== 'POA';
    const confirmed = Boolean(row.price_confirmed_at);
    const set = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));

    const summary = () => {
        const label = PRICE_TYPES.find((t) => t.value === row.price_type)?.label;
        if (!label || !isSet) return 'Not set';
        const unit = row.price_unit ? ` ${row.price_unit}` : '';
        if (row.price_type === 'RANGE' && row.price_max) {
            return `£${row.price} to £${row.price_max}${unit}`;
        }
        return `${label} £${row.price}${unit}`;
    };

    const onOpen = () => {
        setForm(fromRow(row));
        setOpen(true);
    };

    const save = (extra = {}) => {
        setSaving(true);
        dispatch(updatePracticeTreatment({
            practice: Number(practiceId),
            price_type: form.price_type,
            // A top price only means anything on a RANGE. Sending a stale one on
            // a type the user just moved away from is how a fixed price ends up
            // carrying a ghost maximum.
            price_max: form.price_type === 'RANGE' && form.price_max !== ''
                ? form.price_max
                : null,
            price_unit: form.price_unit,
            price_note: form.price_note,
            ...extra,
        }, row.id, () => {
            setSaving(false);
            setOpen(false);
            if (onSaved) onSaved();
        }));
    };

    return (
        <>
            <Stack direction="row" spacing={0.5} alignItems="center">
                <Tooltip title="How the WhatsApp AI may quote this price">
                    <IconButton size="small" onClick={onOpen}>
                        <Iconify icon={isSet
                            ? 'solar:tag-price-bold-duotone'
                            : 'eva:plus-circle-outline'}
                        />
                    </IconButton>
                </Tooltip>
                {isSet && (
                    <Chip
                        size="small"
                        variant="outlined"
                        color={confirmed ? 'success' : 'default'}
                        label={summary()}
                        onClick={onOpen}
                    />
                )}
            </Stack>

            <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>
                    {row.treatment_item?.name}
                    <Typography variant="body2" color="text.secondary">
                        How the WhatsApp AI may quote this. The price itself stays
                        on the Treatments screen and is unchanged by anything here.
                    </Typography>
                </DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2.5} sx={{ mt: 1 }}>
                        <TextField
                            select fullWidth size="small" label="Price type"
                            value={form.price_type}
                            onChange={(e) => set('price_type', e.target.value)}
                            helperText={PRICE_TYPES.find((t) => t.value === form.price_type)?.hint}
                        >
                            {PRICE_TYPES.map((t) => (
                                <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                            ))}
                        </TextField>

                        {form.price_type === 'RANGE' && (
                            <TextField
                                fullWidth size="small" label={`Up to (the price is £${row.price})`}
                                value={form.price_max}
                                onChange={(e) => set('price_max', e.target.value.replace(/[^0-9.]/g, ''))}
                                error={form.price_max !== '' && Number(form.price_max) <= Number(row.price || 0)}
                                helperText={form.price_max !== '' && Number(form.price_max) <= Number(row.price || 0)
                                    ? 'The top of the range must be above the bottom.'
                                    : ' '}
                            />
                        )}

                        {form.price_type !== 'POA' && (
                            <>
                                <TextField
                                    fullWidth size="small" label="Charged per"
                                    placeholder="tooth, arch, course…"
                                    value={form.price_unit}
                                    onChange={(e) => set('price_unit', e.target.value)}
                                    helperText="Leave blank if the price covers the whole treatment."
                                />
                                <TextField
                                    fullWidth size="small" label="What it includes"
                                    placeholder="includes x-rays"
                                    value={form.price_note}
                                    onChange={(e) => set('price_note', e.target.value)}
                                />
                                <Box>
                                    <FormControlLabel
                                        control={(
                                            <Switch
                                                checked={confirmed}
                                                onChange={(e) => save({ price_confirmed: e.target.checked })}
                                            />
                                        )}
                                        label="Somebody here has signed this figure off"
                                    />
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        {confirmed
                                            ? 'A fixed price may be quoted as a fixed fee.'
                                            : 'Until it is signed off the AI says “from” rather than a firm figure. Changing the price clears the sign-off, because nobody has approved the new one.'}
                                    </Typography>
                                </Box>
                            </>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Cancel</Button>
                    <Button variant="contained" disabled={saving} onClick={() => save()}>
                        {saving ? 'Saving…' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
